import React from "react";

import { Info } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { UrlParamTypes } from "../../../../types/org-display";
import { useTrainingMigrationContext } from "./context/training-migration-provider";

export const StartTrainingSyncOrgForm: React.FC = () => {
  const theme = useTheme();
  const { roleId } = useParams<UrlParamTypes>();
  const { syncOrgFormState, setSyncOrgFormState } =
    useTrainingMigrationContext();

  return (
    <Grid container paddingTop={2}>
      <Grid item xs={12} container direction="column">
        <Box
          bgcolor={theme.palette.info.light}
          borderRadius="5px"
          border={1}
          borderColor={theme.palette.info.main}
          padding={2}
          data-testid="sync-org-info-panel"
        >
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid item>
              <Info color="info" fontSize="large" />
            </Grid>
            <Grid item xs={10}>
              <Typography variant="body2" component="div">
                Re-sends this organisation&apos;s current state to Training. It
                does not change where training is hosted and does not migrate
                any data.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required disabled>
            Organisation ID
          </FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Organisation ID"
            inputProps={{
              "data-testid": "input-organisation-id",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={roleId}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={9}>
          <FormLabel required>Region</FormLabel>
        </Grid>

        <Grid item xs={3}>
          <TextField
            name="Region"
            placeholder="AU, US, EU"
            inputProps={{
              "data-testid": "input-region",
            }}
            error={syncOrgFormState.region === ""}
            fullWidth
            margin="normal"
            variant="outlined"
            onChange={(e) =>
              setSyncOrgFormState({
                ...syncOrgFormState,
                region: e.target.value,
              })
            }
            value={syncOrgFormState.region}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Reference</FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Reference"
            placeholder="Enter a reference for this sync request (optional)"
            inputProps={{
              "data-testid": "input-reference",
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={syncOrgFormState.reference}
            onChange={(e) =>
              setSyncOrgFormState({
                ...syncOrgFormState,
                reference: e.target.value,
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Sync organisation data only</FormLabel>
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={syncOrgFormState.syncOrgDataOnly}
            onChange={(e) =>
              setSyncOrgFormState({
                ...syncOrgFormState,
                syncOrgDataOnly: e.target.checked,
              })
            }
            data-testid="input-sync-org-data-only"
            inputProps={{ "aria-label": "Sync organisation data only" }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">
            Republishes the organisation&apos;s plan tier, language and
            user-field definitions to Training. Fast. Untick to also re-sync
            every member&apos;s permissions, timezone, language and attributes,
            which takes longer on large organisations.
          </Typography>
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Also sync groups and sites</FormLabel>
        </Grid>
        <Grid item xs={7}>
          <Checkbox
            checked={syncOrgFormState.syncOrgDirectory}
            onChange={(e) =>
              setSyncOrgFormState({
                ...syncOrgFormState,
                syncOrgDirectory: e.target.checked,
              })
            }
            data-testid="input-sync-org-directory"
            inputProps={{ "aria-label": "Also sync groups and sites" }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">
            Re-sends a full-sync event for every group and site in the
            organisation. Use when folders or groups are missing in Training.
            Not reliable for organisations with more than 1000 groups.
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};
